import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { api, type SimulationKind } from '../../api/client';
import type { RecommendedAction } from '../../types';
import Code, { CodeText } from '../Code';
import DecisionPanel from '../DecisionPanel';

interface Props {
  view: string;
  state: Record<string, unknown>;
  headline: string;
  action: RecommendedAction | null;
  readOnlyAction?: boolean;
}

const SIGNAL_BY_OPTION: Record<string, string> = {
  'O-01': 'S-10',
  'O-02': 'S-01',
  'O-03': 'S-06',
  'O-04': 'S-02',
  'O-05': 'S-04',
  'O-06': 'S-12',
  'O-07': 'S-03',
  'O-08': 'S-09',
  'O-09': 'S-05',
  'O-10': 'S-08',
  'O-11': 'S-07',
  'O-14': 'S-11',
  'O-15': 'S-09',
  'O-16': 'S-12',
  'O-17': 'S-02',
};

const OWNER_BY_RULE_PREFIX: Record<string, string> = {
  innovation: 'Growth / R&D',
  sourcing: 'CFO / Procurement',
  manufacturing: 'COO / Plant Ops',
  distribution: 'Sales / Logistics',
  gtm: 'Brand / DTC / Sales',
  mna: 'Corp Dev / Supply Chain',
  simulation: 'Strategy / CEO Staff',
};

export default function HeadlineActionBand({ view, state, headline, action, readOnlyAction = false }: Props) {
  const owner = useMemo(() => ownerForAction(action, state), [action, state]);

  return (
    <div
      className="panel"
      style={{
        marginBottom: 14,
        padding: '12px 16px',
        background: action ? 'rgba(214,162,58,0.07)' : 'var(--panel)',
        borderLeft: `3px solid ${action ? 'var(--amber)' : 'var(--line)'}`,
      }}
    >
      <div style={{ fontSize: 15, lineHeight: 1.5, color: 'var(--ink)' }}>{headline}</div>

      {action && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--line-soft)' }}>
          <div className="row" style={{ gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <span className="tag amber" style={{ fontSize: 12 }}>RECOMMENDED</span>
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}><CodeText text={action.text} compact /></span>
          </div>
          <div className="row" style={{ gap: 12, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Link to={action.disposition_route} className="btn ghost" style={{ fontSize: 12, padding: '4px 10px', textDecoration: 'none' }}>
              Open ↗
            </Link>
            {action.option_ref && (
              <span className="small faint" style={{ fontSize: 12 }}>
                option · <Code id={action.option_ref} kind="intervention" compact />
              </span>
            )}
            <span className="small faint" style={{ fontSize: 12 }}>owner · {owner}</span>
          </div>

          {!readOnlyAction && (
            <DecisionPanel
              key={action.rule_id}
              embedded
              owner={owner}
              commit={(disposition, envelope) =>
                api.simulationDisposition({
                  simulation_kind: simulationKindForAction(action, view),
                  signal_id: signalIdForAction(action, state),
                  disposition,
                  owner,
                  recommendation: action.text,
                  predicted_outcome: {
                    source: 'headline_band',
                    view,
                    rule_id: action.rule_id,
                    entities: action.entities ?? [],
                    option_ref: action.option_ref ?? null,
                    disposition_route: action.disposition_route,
                  },
                  decision: envelope,
                })
              }
            />
          )}
        </div>
      )}
    </div>
  );
}

function ownerForAction(action: RecommendedAction | null, state: Record<string, unknown>): string {
  const owner = stringFromState(state, 'owner');
  if (owner) return owner;
  const prefix = action?.rule_id.split('.')[0] ?? '';
  return OWNER_BY_RULE_PREFIX[prefix] ?? 'Operator';
}

function signalIdForAction(action: RecommendedAction, state: Record<string, unknown>): string {
  const stateSignal = stringFromState(state, 'signal_id') ?? stringFromNestedState(state, 'signal', 'id');
  if (stateSignal) return stateSignal;
  if (action.option_ref && SIGNAL_BY_OPTION[action.option_ref]) return SIGNAL_BY_OPTION[action.option_ref];
  const prefix = action.rule_id.split('.')[0];
  if (prefix === 'sourcing') return 'S-01';
  if (prefix === 'manufacturing') return 'S-12';
  if (prefix === 'distribution') return 'S-09';
  if (prefix === 'gtm') return 'S-06';
  if (prefix === 'innovation') return 'S-10';
  if (prefix === 'mna') return 'S-09';
  return 'S-02';
}

function simulationKindForAction(action: RecommendedAction, view: string): SimulationKind {
  const rule = action.rule_id;
  const route = action.disposition_route;
  if (rule.includes('pricing') || route.includes('trade_control') || route.includes('cost_to_serve')) return 'pricing_mix';
  if (rule.startsWith('sourcing.') || route.includes('profit_at_risk')) return 'profit_at_risk';
  if (rule.startsWith('distribution.') || rule.startsWith('mna.') || route.includes('resilience')) return 'sector_shock';
  if (rule.startsWith('simulation.') || view === 'engine:6') return 'wargame';
  return 'pre_mortem';
}

function stringFromState(state: Record<string, unknown>, key: string): string | null {
  const value = state[key];
  return typeof value === 'string' && value.trim() ? value : null;
}

function stringFromNestedState(state: Record<string, unknown>, parent: string, key: string): string | null {
  const value = state[parent];
  if (!value || typeof value !== 'object') return null;
  const child = (value as Record<string, unknown>)[key];
  return typeof child === 'string' && child.trim() ? child : null;
}
